/* Phase E.2 — 3C : drawer « Actions en lot » (génération multilingue par lot).
   AUCUNE règle métier recalculée localement : éligibilité, quota, hard cap,
   statuts et idempotence sont la vérité du backend (preview/launch).
   Périmètre : generate_missing / regenerate uniquement — jamais de publication,
   d'images, d'ALT ni de calendrier ici. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, X } from "lucide-react";
import BatchProgressPanel, {
  genIdempotencyKey, normalizeError, reasonLabel,
} from "@/components/BatchProgressPanel";

export default function BatchActionsDrawer({ open, onClose, site, parents, onLaunched }) {
  const [langs, setLangs] = useState(null);
  const [operation, setOperation] = useState(null);
  const [sel, setSel] = useState({});
  const [idemKey, setIdemKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [activeBatch, setActiveBatch] = useState(null);
  // Ref miroir : une fois un lot lancé, l'init du drawer ne doit JAMAIS écraser le suivi
  const activeBatchRef = useRef(null);

  const parentsById = useMemo(
    () => Object.fromEntries((parents || []).map((p) => [p.id, p])), [parents]);
  const siteId = site?.id;
  // Clé STABLE de la sélection (ids) — jamais la référence du tableau `parents` :
  // un simple re-render/refresh de la liste ne doit pas réinitialiser le drawer.
  const parentsKey = useMemo(() => (parents || []).map((p) => p.id).join("|"), [parents]);
  const parentsRef = useRef(parents);
  parentsRef.current = parents;

  useEffect(() => {
    if (!open) {
      activeBatchRef.current = null;
      setActiveBatch(null);
      return undefined;
    }
    if (!siteId) return undefined;
    if (activeBatchRef.current) return undefined; // lot lancé : suivi préservé
    setOperation(null);
    setPreview(null);
    setPreviewError(null);
    setLaunchError(null);
    setActiveBatch(null);
    setIdemKey(genIdempotencyKey()); // nouvelle ouverture/sélection = nouvelle clé
    setLangs(null);
    let cancelled = false;
    api.get(`/sites/${siteId}/languages`)
      .then(({ data }) => {
        if (cancelled) return;
        setLangs(data);
        const available = data?.available_languages || [];
        const init = {};
        for (const p of parentsRef.current || []) {
          init[p.id] = {};
          for (const l of p.locales || []) {
            // locale désactivée : jamais cochée par défaut, jamais envoyée par défaut
            init[p.id][l.locale] = available.includes(l.locale);
          }
        }
        setSel(init);
      })
      .catch(() => { if (!cancelled) setLangs({ available_languages: [] }); });
    return () => { cancelled = true; };
  }, [open, siteId, parentsKey]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /* Tout changement d'opération/sélection INVALIDE le preview :
     launch masqué + nouvelle demande = nouvelle clé d'idempotence. */
  const invalidatePreview = useCallback(() => {
    setPreview(null);
    setPreviewError(null);
    setLaunchError(null);
    setIdemKey(genIdempotencyKey());
  }, []);

  const chooseOperation = (op) => {
    setOperation(op);
    invalidatePreview();
  };

  const toggleLocale = (parentId, locale) => {
    setSel((prev) => ({
      ...prev,
      [parentId]: { ...(prev[parentId] || {}), [locale]: !(prev[parentId] || {})[locale] },
    }));
    invalidatePreview();
  };

  const pairs = useMemo(() => {
    const out = [];
    for (const p of parents || []) {
      const s = sel[p.id] || {};
      for (const l of p.locales || []) {
        if (s[l.locale]) out.push({ parent_id: p.id, locale: l.locale });
      }
    }
    return out;
  }, [parents, sel]);

  const runPreview = async () => {
    if (!operation || pairs.length === 0 || previewLoading) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setLaunchError(null);
    try {
      const { data } = await api.post("/content-batches/preview", {
        site_id: site.id, operation_type: operation, items: pairs,
      });
      setPreview(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setPreviewError(normalizeError(typeof d === "string" ? d : d?.message)
        || "Prévisualisation impossible");
    } finally {
      setPreviewLoading(false);
    }
  };

  const eligibleItems = useMemo(
    () => (preview?.items || []).filter((i) => i.eligibility === "ELIGIBLE"), [preview]);
  const quotaOk = preview ? preview.quota_check?.ok !== false : true;
  const capExceeded = !!previewError && /dépasse la limite/i.test(previewError);
  const canLaunch = !!preview && eligibleItems.length > 0 && quotaOk && !launching;

  const launch = async () => {
    if (!canLaunch) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const items = eligibleItems.map((i) => ({
        parent_id: i.parent_id,
        locale: i.locale,
        // transmis EXACTEMENT tel que renvoyé par le preview — jamais recalculé
        expected_locale_updated_at: i.expected_locale_updated_at,
      }));
      const { data } = await api.post("/content-batches", {
        site_id: site.id, operation_type: operation,
        idempotency_key: idemKey, items,
      });
      // 201 (créé) et 200 (rejeu idempotent) sont les DEUX des succès UX
      activeBatchRef.current = data;
      setActiveBatch(data);
      onLaunched?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "IDEMPOTENCY_KEY_REUSED_WITH_DIFFERENT_REQUEST") {
        // jamais de retry automatique avec la même clé, jamais de nouvelle clé silencieuse
        setLaunchError("Cette demande a changé depuis son lancement. Fermez puis recréez le lot.");
      } else {
        setLaunchError(normalizeError(typeof d === "string" ? d : d?.message)
          || "Lancement impossible");
      }
    } finally {
      setLaunching(false);
    }
  };

  if (!open) return null;
  const available = langs?.available_languages || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="content-batch-drawer"
         role="dialog" aria-modal="true" aria-label="Actions en lot — génération multilingue">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white w-full max-w-xl h-full shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Actions en lot</h2>
            <p className="text-[11px] text-slate-500">
              {parents?.length || 0} contenu{(parents?.length || 0) > 1 ? "s" : ""} sélectionné{(parents?.length || 0) > 1 ? "s" : ""} · site {site?.name || site?.label}
            </p>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="content-batch-drawer-close"
            className="text-slate-400 hover:text-slate-700 p-1.5"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {activeBatch ? (
            <BatchProgressPanel batchId={activeBatch.id} parentsById={parentsById}
              onRetryLaunched={(b) => { activeBatchRef.current = b; setActiveBatch(b); onLaunched?.(b); }} />
          ) : (
            <>
              <fieldset>
                <legend className="text-xs font-semibold text-slate-700 mb-2">1. Opération</legend>
                <div className="grid grid-cols-1 gap-2">
                  <button type="button" data-testid="content-batch-operation-generate"
                    onClick={() => chooseOperation("generate_missing")}
                    aria-pressed={operation === "generate_missing"}
                    className={`text-left border rounded-md px-3 py-2.5 text-sm ${operation === "generate_missing" ? "border-[#002FA7] bg-blue-50 text-[#002FA7] font-medium" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                    Générer les langues manquantes
                    <span className="block text-[11px] text-slate-500 font-normal">
                      Uniquement les langues non générées ou en erreur.
                    </span>
                  </button>
                  <button type="button" data-testid="content-batch-operation-regenerate"
                    onClick={() => chooseOperation("regenerate")}
                    aria-pressed={operation === "regenerate"}
                    className={`text-left border rounded-md px-3 py-2.5 text-sm ${operation === "regenerate" ? "border-[#002FA7] bg-blue-50 text-[#002FA7] font-medium" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                    Régénérer les contenus existants
                    <span className="block text-[11px] text-slate-500 font-normal">
                      Remplace les brouillons existants (jamais les contenus en cours de génération ou publiés).
                    </span>
                  </button>
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-xs font-semibold text-slate-700 mb-2">2. Langues par contenu</legend>
                {!langs ? (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des langues du site…
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(parents || []).map((p) => (
                      <div key={p.id} className="border border-slate-200 rounded-md p-3"
                           data-testid={`content-batch-locales-${p.id}`}>
                        <div className="text-xs font-medium text-slate-800 truncate mb-1.5">{p.topic}</div>
                        <div className="flex flex-wrap gap-3">
                          {(p.locales || []).map((l) => {
                            const enabled = available.includes(l.locale);
                            const checked = !!(sel[p.id] || {})[l.locale];
                            const inputId = `batch-loc-${p.id}-${l.locale}`;
                            return (
                              <label key={l.locale} htmlFor={inputId}
                                className={`inline-flex items-center gap-1.5 text-xs ${enabled ? "text-slate-700 cursor-pointer" : "text-slate-400"}`}>
                                <input id={inputId} type="checkbox" checked={checked}
                                  disabled={!enabled}
                                  onChange={() => toggleLocale(p.id, l.locale)}
                                  data-testid={`content-batch-locale-${p.id}-${l.locale}`}
                                  className="rounded border-slate-300" />
                                <span className="font-mono">{l.locale}</span>
                                {!enabled && <span className="text-[10px]">(désactivée)</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </fieldset>

              <div>
                <button onClick={runPreview} data-testid="content-batch-preview"
                  disabled={!operation || pairs.length === 0 || previewLoading}
                  className="inline-flex items-center gap-2 text-sm font-medium border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Prévisualiser le lot
                </button>
                {!operation && (
                  <p className="text-[11px] text-slate-400 mt-1">Choisissez d&apos;abord une opération.</p>
                )}
              </div>

              {previewError && (
                <div role="alert" data-testid={capExceeded ? "content-batch-cap-error" : "content-batch-preview-error"}
                  className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {capExceeded && (
                    <span className="block font-semibold mb-0.5">
                      Ce lot dépasse la limite maximale de contenus/langues autorisée.
                    </span>
                  )}
                  {previewError}
                </div>
              )}

              {preview && (
                <div className="space-y-3" data-testid="content-batch-preview-result">
                  <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
                    <div className="text-sm font-semibold text-slate-900" data-testid="content-batch-units">
                      {preview.generation_units} contenu{preview.generation_units > 1 ? "s" : ""}/langue{preview.generation_units > 1 ? "s" : ""} à générer
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5" data-testid="content-batch-attempts-note">
                      Jusqu&apos;à {preview.generation_llm_attempts_per_unit} tentatives de génération par contenu en cas de réponse invalide
                      · limite : {preview.max_batch_units} contenus/langues par lot.
                    </div>
                  </div>
                  {!quotaOk && (
                    <div role="alert" data-testid="content-batch-quota-warning"
                      className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      Quota actuellement insuffisant — {preview.quota_check?.detail}
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {(preview.items || []).map((i) => (
                      <div key={`${i.parent_id}-${i.locale}`}
                        data-testid={`content-batch-preview-item-${i.parent_id}-${i.locale}`}
                        data-eligibility={i.eligibility} data-reason={i.reason || ""}
                        className="flex items-center gap-2 text-xs border border-slate-100 rounded-md px-2.5 py-1.5">
                        <span className="font-mono font-semibold text-slate-700">{i.locale}</span>
                        <span className="text-slate-500 truncate flex-1 min-w-0">
                          {parentsById[i.parent_id]?.topic || i.parent_id}
                        </span>
                        {i.eligibility === "ELIGIBLE" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Prêt
                          </span>
                        ) : (
                          <span className="text-slate-500" title={i.reason}>
                            Non exécutable — {reasonLabel(i.reason)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {launchError && (
                <div role="alert" data-testid="content-batch-launch-error"
                  className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {launchError}
                </div>
              )}
            </>
          )}
        </div>

        {!activeBatch && (
          <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between gap-3">
            <p className="text-[11px] text-slate-400">
              La prévisualisation est obligatoire avant le lancement.
            </p>
            <button onClick={launch} disabled={!canLaunch} data-testid="content-batch-launch"
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#001D6B] text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
              {launching && <Loader2 className="w-4 h-4 animate-spin" />}
              Lancer le lot
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
